import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, MessageCircle, Instagram, User, LogIn, Sun, Moon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import logoLight from '../assets/images/logo_avenida_escuro.png';
import logoDark from '../assets/images/logo-avenida-veiculos.png';

const WHATSAPP_URL = 'https://wa.me/5551995207441';
const WHATSAPP_NUMBER = '(51) 99520-7441';
const INSTAGRAM_URL = 'https://www.instagram.com/avenida_veiculos_oficial_';
const INSTAGRAM_HANDLE = '@avenida_veiculos_oficial_';

export const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const navigation = [
    { name: 'Início', href: '/' },
    { name: 'Estoque', href: '/inventory' },
    { name: 'Contato', href: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="navbar">
      <div className="container-custom">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="navbar-brand">
            <img 
              src={theme === 'dark' ? logoDark : logoLight}
              alt="Avenida Veículos" 
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="navbar-nav">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`navbar-link ${isActive(item.href) ? 'active' : ''}`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Theme Toggle */}
            <button
              type="button"
              onClick={toggleTheme}
              className="p-2 rounded-full border border-transparent text-gray-600 dark:text-gray-300 hover:text-primary hover:border-primary/40 transition-colors"
              aria-label={theme === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>

            {/* Contact Info - WhatsApp & Instagram */}
            <div className="flex items-center space-x-4 text-sm">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-primary transition-colors"
              >
                <MessageCircle className="h-4 w-4 text-primary" />
                <span className="hidden xl:inline">{WHATSAPP_NUMBER}</span>
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-primary transition-colors"
              >
                <Instagram className="h-4 w-4 text-primary" />
                <span className="hidden xl:inline">{INSTAGRAM_HANDLE}</span>
              </a>
            </div>
            
            {/* Admin Login Button */}
            {isAuthenticated ? (
              <Link 
                to="/admin/dashboard" 
                className="btn btn-primary btn-sm"
              >
                <User className="h-4 w-4 mr-2" />
                Dashboard
              </Link>
            ) : (
              <Link 
                to="/admin/login" 
                className="btn btn-outline btn-sm"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Admin
              </Link>
            )}
            
            {/* CTA Button */}
            <Link 
              to="/contact" 
              className="btn btn-primary btn-sm"
            >
              <User className="h-4 w-4 mr-2" />
              Fale Conosco
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-gray-600 dark:text-gray-300 hover:text-primary transition-colors"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white dark:bg-dark-800 rounded-lg mt-2 border border-gray-200 dark:border-dark-700">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isActive(item.href)
                      ? 'text-primary bg-primary/10'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary hover:bg-gray-100 dark:hover:bg-dark-700'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Contact Info */}
              <div className="px-3 py-2 space-y-3 border-t border-gray-200 dark:border-dark-700 mt-4 pt-4">
                {/* Theme Toggle Mobile */}
                <button
                  type="button"
                  onClick={() => {
                    toggleTheme();
                  }}
                  className="w-full flex items-center justify-center px-3 py-2 mb-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
                >
                  {theme === 'dark' ? (
                    <>
                      <Sun className="h-4 w-4 mr-2" />
                      Modo claro
                    </>
                  ) : (
                    <>
                      <Moon className="h-4 w-4 mr-2" />
                      Modo escuro
                    </>
                  )}
                </button>
                {/* Mobile WhatsApp & Instagram */}
                <div className="flex flex-col space-y-3 text-sm text-gray-600 dark:text-gray-300">
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 hover:text-primary transition-colors"
                  >
                    <MessageCircle className="h-4 w-4 text-primary" />
                    <span>{WHATSAPP_NUMBER}</span>
                  </a>
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 hover:text-primary transition-colors"
                  >
                    <Instagram className="h-4 w-4 text-primary" />
                    <span>{INSTAGRAM_HANDLE}</span>
                  </a>
                </div>
                {/* Mobile Admin Button */}
                {isAuthenticated ? (
                  <Link 
                    to="/admin/dashboard" 
                    className="btn btn-primary w-full justify-center mt-4"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <User className="h-4 w-4 mr-2" />
                    Dashboard
                  </Link>
                ) : (
                  <Link 
                    to="/admin/login" 
                    className="btn btn-outline w-full justify-center mt-4"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <LogIn className="h-4 w-4 mr-2" />
                    Admin
                  </Link>
                )}
                
                <Link 
                  to="/contact" 
                  className="btn btn-primary w-full justify-center mt-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <User className="h-4 w-4 mr-2" />
                  Fale Conosco
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};